declare var require: any; // TODO: chrono-node is not a module, fix this

import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Entry } from 'src/services/entry/Entry';
import { EntryService } from 'src/services/entry/entry.service';
import { TagService } from 'src/services/tag/tag.service';
import {
  getTopLevelTag,
  getTopLevelTagByFullPath,
} from 'src/utils/getTopLevelTag';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { TagListComponent } from '../tag-list/tag-list.component';
import { DetailedEntry } from 'src/services/entry/EntryWithDetails';
import { getTagColor } from 'src/utils/getTagColor';
import { trackByTagId } from 'src/utils/trackByTagId';
import { PrefundCurrencyPipe } from 'src/app/prefund-currency.pipe';
import { removeParents } from 'src/utils/removeParents';
import { PeriodService } from 'src/app/services/period.service';
import { Tag } from 'src/services/tag/Tag';

const chrono = require('chrono-node');

export interface AddData {
  data: {
    tag: string;
    tags: DetailedTag[];
    entry?: Entry;
  };
}

@Component({
  selector: 'add',
  templateUrl: './add.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    FormsModule,
    TagListComponent,
    PrefundCurrencyPipe,
  ],
  styleUrls: ['./add.component.scss'],
})
export class AddComponent {
  entry: (Entry & { tagFullPaths: string[] }) | null = null;
  tags: DetailedTag[] = [];
  filteredTags: DetailedTag[] = [];
  isInitialized = true;
  private readonly NEGATIVE_BY_DEFAULT = true;

  getTopLevelTag = getTopLevelTag;
  getTagColor = getTagColor;
  trackByTagId = trackByTagId;

  options: string[] = ['One', 'Two', 'Three'];

  @ViewChild(MatAutocompleteTrigger)
  autocomplete: MatAutocompleteTrigger | null = null;

  public isAutoCompleteOpen = false;

  @ViewChild('textarea') textarea: ElementRef | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddData,
    private matDialogRef: MatDialogRef<AddComponent>,
    private entryService: EntryService,
    private tagService: TagService,
    private _snackBar: MatSnackBar,
    private periodService: PeriodService
  ) {}

  ngOnInit() {
    if (this.data.data.entry) {
      this.entry = {
        ...this.data.data.entry,
        tagFullPaths: this.data.data.entry.tagIds
          .map(
            (ref) => this.data.data.tags.find((t) => t.id === ref)?.fullPath!
          )
          .filter((t) => t !== undefined),
      };
      this.setupModal();
    } else {
      if (this.data.data.tag) {
        setTimeout(() => {
          this.setupModal();
          this.localInput = `#${this.data.data.tag} `;
          this.textarea!.nativeElement.value = this.localInput;
          this.entry = this.parse(this.localInput);
        }, 0);
      } else {
        this.setupModal();
        this.entry = this.parse('');
      }
    }
  }

  setupModal() {
    this.tags = this.data.data.tags;
    this.filteredTags = this.tags;
    setTimeout(() => this.closeAutocompleteAndSetupInputIfNecessary());
  }

  closeAutocompleteAndSetupInputIfNecessary() {
    this.autocomplete!.autocompleteDisabled = true;
    this.autocomplete!.closePanel();

    if (this.data.data.entry) {
      const date = this.data.data.entry.date
        ? new Date(this.data.data.entry.date)
        : new Date();
      const formattedDate = date.toISOString().split('T')[0];
      const tags = this.data.data.entry.tagIds
        .map((ref) => {
          const tagFullPath = this.data.data.tags.find(
            (t) => t.id === ref
          )?.fullPath;

          return tagFullPath ? `#${tagFullPath}` : undefined;
        })
        .filter((t) => t !== undefined);

      const amount =
        this.data.data.entry.amount < 0
          ? `${this.data.data.entry.amount}`
          : `+${this.data.data.entry.amount}`;

      this.textarea!.nativeElement.value = `${amount} ${
        this.data.data.entry.memo
      } ${tags.join(' ')}  ${formattedDate}`;
    }

    setTimeout(() => {
      this.isInitialized = true;
    }, 0);
  }

  public isEmpty = true;

  updatePreview(event: Event | { target: { value: string } }) {
    const target = event.target as HTMLInputElement;
    this.entry = this.parse(target.value as string);
    this.isEmpty = !(target.value && target.value.length > 0);
  }

  parse(input: string): Entry & { tagFullPaths: string[] } {
    let parsed = ''.concat(input);

    // First, parse hashtags and remove from input
    const hashtags: string[] = parsed.match(/#\w+([\/\w\-\:]*)/g) ?? [];
    parsed = parsed.replace(/#\w+([\/\w\-\:]*)/g, '');

    // Second, parse amount and remove from input
    const firstNumberInInputString =
      parsed.match(/(?<!#)([-+]?\.\d+|[-+]?\d+(\.\d+)?)/)?.[0] ?? '0';

    const firstNumberInInput = firstNumberInInputString.includes('+')
      ? +firstNumberInInputString
      : firstNumberInInputString.includes('-')
      ? +firstNumberInInputString
      : this.NEGATIVE_BY_DEFAULT
      ? +firstNumberInInputString * -1
      : +firstNumberInInputString;

    let memo = parsed.replace(firstNumberInInputString, '').trim();

    // Third, parse date and remove from memo
    const result = chrono.parse(memo);

    let memoWithoutParsedDate =
      result.length > 0 ? memo.replace(result[0].text, '') : memo;

    // Fourth reset cursor position
    this.localInput = input;

    this.localInputCursorPosition =
      this.textarea?.nativeElement.selectionStart ?? 0;

    // Returned parsed entry
    return {
      amount: firstNumberInInput ?? 0,
      memo: memoWithoutParsedDate,
      id: this.entry?.id,
      tagIds: [],
      tagFullPaths: Array.from(
        new Set([...hashtags.map((h) => h.replace('#', ''))])
      ),
      date:
        result.length > 0
          ? result[0]?.date()
          : this.periodService.getDefaultDateForEntry(
              this.getTag(this.data.data.tag),
              this.data.data.tags
            ),
    };
  }

  getTag(tagFullPath: string): DetailedTag {
    return this.tags.find((t) => t.fullPath === tagFullPath)!;
  }

  getColorClass(tagFullPath: string): string {
    const topLevelTag = getTopLevelTagByFullPath(tagFullPath, this.tags);
    if (topLevelTag) {
      return topLevelTag.color;
    } else {
      return TagService.DEFAULT_COLOR;
    }
  }

  openDatePicker() {
    // alert('opening date picker not yet working');
  }

  close() {
    this.matDialogRef.close();
  }

  public async save(shouldClose = true) {
    try {
      await AddComponent.saveEntry(
        this.entry!,
        this.tags!,
        this.tagService,
        this.entryService,
        this._snackBar
      );

      if (shouldClose) {
        this.matDialogRef.close();
      } else {
        this.textarea!.nativeElement.value = '';

        this.updatePreview({ target: { value: '' } });
      }
    } catch (e: any) {
      this._snackBar.open(e?.message);
      throw e;
    }
  }

  public static async saveEntry(
    entry: Entry & { tagFullPaths: string[] },
    tags: DetailedTag[],
    tagService: TagService,
    entryService: EntryService,
    _snackBar: MatSnackBar
  ) {
    entry!.tagFullPaths = removeParents(entry!.tagFullPaths);

    entry!.tagIds =
      entry?.tagFullPaths.map(
        (ref) => tags.find((t) => t.fullPath === ref)?.id!
      ) ?? [];

    const fullPathsToCreate = entry.tagFullPaths.filter(
      (fullPath) => !tags.some((t) => t.fullPath === fullPath)
    );

    if (fullPathsToCreate) {
      const refs: string[] = await Promise.all(
        fullPathsToCreate.map(async (fullPath) => {
          return await tagService.create(fullPath);
        })
      );

      entry.tagIds.push(...refs);
    }

    const colors = entry?.tagFullPaths
      .map((fullPath) => {
        return (
          getTopLevelTagByFullPath(fullPath, tags)?.color ??
          TagService.DEFAULT_COLOR
        );
      })
      .map((c) => {
        switch (c) {
          case 'green':
            return '#86efac';
          case 'yellow':
            return '#eab308';
          case 'blue':
            return '#3b82f6';
          case 'purple':
            return '#a855f7';
          case 'red':
            return '#ef4444';
          case 'orange':
            return '#f97316';
          case 'white':
            return '#6b7280';
          default:
            return '#86efac'; // green
        }
      });

    if (entry?.id) {
      await entryService.put(entry);
      _snackBar.open('🎉 Entry updated');
      AddComponent.showConfetti(colors);
    } else {
      await entryService.post(entry!);
      _snackBar.open('🎉 Entry created');
      AddComponent.showConfetti(colors);
    }
  }

  public static showConfetti(colors: string[] = []) {
    // @ts-ignore
    window.confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 1 },
      colors: colors && colors.length > 0 ? colors : ['#6b7280'],
    });
  }

  submit() {
    if (!this.autocomplete?.panelOpen) {
      this.save();
    } else {
      this.autocomplete?.closePanel();
    }
  }

  async onKeydown(event: KeyboardEvent) {
    if (event.key === '#') {
      this.openDropdown();
    } else if (
      ['Escape', 'ArrowLeft', 'ArrowRight', ' ', 'Backspace'].includes(
        event.key
      )
    ) {
      if (this.isAutoCompleteOpen && event.key === 'Escape') {
        event.stopPropagation(); // do not close the modal
        this.closeDropdown();
      } else {
        this.closeDropdown();
      }
    } else if (this.autocomplete?.panelOpen) {
      if (/^[a-zA-Z0-9/]$/.test(event.key)) {
        this.hashtagFilter = (this.hashtagFilter ?? '').concat(event.key);
        this.filteredTags = this.tags.filter(
          (t) => this.hashtagFilter && t.fullPath.includes(this.hashtagFilter)
        );
      }
    } else {
      this.filteredTags = this.tags;
      this.closeDropdown();
    }
  }

  private hashtagFilter: string | null = null;

  openDropdown() {
    this.autocomplete!.autocompleteDisabled = false;
    this.hashtagFilter = null;
    this.filteredTags = [...this.tags];
  }

  closeDropdown() {
    this.autocomplete?.closePanel();

    this.autocomplete!.autocompleteDisabled = true;
    this.hashtagFilter = null;
    this.filteredTags = this.tags;
  }

  private localInput: string | null = null;
  private localInputCursorPosition: number | null = null;

  addTagFromAutocomplete(event: MatAutocompleteSelectedEvent) {
    const textWithoutUnfinishedHashtag = this.removeWordAtPosition(
      this.localInput!,
      this.localInputCursorPosition!
    );

    const textWithFinishedHashtag = this.addWordAtPosition(
      textWithoutUnfinishedHashtag,
      `#${event.option.value.fullPath} `,
      this.localInputCursorPosition!
    );

    this.textarea!.nativeElement.selectionStart = 0;

    this.textarea!.nativeElement.value = textWithFinishedHashtag;

    this.entry = this.parse(textWithFinishedHashtag); // almost good
  }

  removeWordAtPosition(text: string, position: number) {
    if (text.length === 0) {
      return text;
    }

    let start = position;
    let end = position;

    // Move start backward to find the beginning of the word
    while (start > 0 && text[start - 1] !== ' ') {
      start--;
    }

    // Move end forward to find the end of the word
    while (end < text.length && text[end] !== ' ') {
      end++;
    }

    // Remove the word from the string
    const newText = text.substring(0, start) + text.substring(end);

    return newText.trim();
  }

  addWordAtPosition(text: string, word: string, position: number): string {
    if (text.length === 0) {
      return word;
    }

    const before = text.substring(0, position);
    const after = text.substring(position);

    // Ensure there are spaces around the new word
    const spaceBefore = before.endsWith(' ') ? '' : ' ';
    const spaceAfter = after.startsWith(' ') ? '' : ' ';

    const addWordAtPosition = before + spaceBefore + word + spaceAfter + after;

    return addWordAtPosition;
  }
}
